import {
  Alert,
  Divider,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Job, JobConflict } from 'src/@types/jobs';
import { useLocales } from 'src/locales';
import React, { useMemo } from 'react';
import Button from '@mui/material/Button';
import { DatePicker } from '@mui/x-date-pickers-pro';
import { format } from 'date-fns';
import Iconify from '../../../components/iconify';
import { CloseIcon } from '../../../theme/overrides/CustomIcons';
import { mapKeycloakErrorMessage } from '../../../utils/mapKeycloakErrorMessage';
import { ConflictAction, ConflictResolution } from '../../../@types/duplicateConflict';
import { buildExcelCustomMappingFromJobPayload } from './ConflictResolutionHelper';
import { useSaveExcelCustomMappingMutation } from 'src/redux/slices/excelImportSlice';

type NotificationInfoPopupProps = {
  notificationContent: Job;
  setShowPopup: (active: boolean) => void;
  notificationType: string;
};

export default function NotificationInfoPopup({
  notificationContent,
  setShowPopup,
  notificationType,
}: NotificationInfoPopupProps) {
  const { translate } = useLocales();

  const content = useMemo(() => {
    switch (notificationType) {
      case 'EXCEL_IMPORT':
        if (notificationContent.status === 'WARNING') {
          return (
            <ExcelImportConflictContent
              notificationContent={notificationContent}
              setShowPopup={setShowPopup}
            />
          );
        }

        return <ExcelImportErrorContent notificationContent={notificationContent} />;

      case 'IMPORT_EMPLOYEES_INTO_KEYCLOAK':
        return <KeycloakImportNotificationContent notificationContent={notificationContent} />;

      default:
        return null;
    }
  }, [notificationType, notificationContent, setShowPopup]);

  let title: string = '';
  if (notificationType === 'EXCEL_IMPORT') {
    const importType = notificationContent.payload.split('#')[1].trim().toUpperCase();
    title = translate(`excelImport.typesError.${importType}`) as string;
  }
  if (notificationType === 'IMPORT_EMPLOYEES_INTO_KEYCLOAK') {
    title = `${translate('employeeApp.import')}`;
  }
  const handleCLose = () => {
    setShowPopup(false);
  };

  return (
    <div>
      <Dialog open onClose={setShowPopup} fullWidth maxWidth="xl" sx={{ mt: 2, height: '100%' }}>
        <DialogTitle>{title}</DialogTitle>
        <Button
          sx={{
            position: 'absolute',
            right: 10,
            top: 10,
          }}
          size="large"
          onClick={() => handleCLose()}
        >
          <CloseIcon />
        </Button>
        <DialogContent sx={{ overflowY: 'scroll' }}>{content}</DialogContent>
      </Dialog>
    </div>
  );
}

type NotificationContentProp = {
  notificationContent: Job;
};

const ExcelImportErrorContent = ({ notificationContent }: NotificationContentProp) => {
  const { translate } = useLocales();

  const importType = notificationContent.payload?.split('#')[1]?.trim() ?? '';

  const parts: string[] = (notificationContent.result ?? '')
    .split('#')
    .sort((a, b) => parseInt(a, 10) - parseInt(b, 10))
    .filter((elem) => elem !== '');

  return (
    <Table size="small" stickyHeader>
      <TableHead>
        <TableRow>
          <TableCell align="center">{`${translate('excelImport.row')}`}</TableCell>
          <TableCell align="center">
            {`${translate(`excelImport.importTypeTitle.${importType}`)}`}
          </TableCell>
          <TableCell align="center">{`${translate('excelImport.columnWithError')}`}</TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {parts.map((row, index) => {
          const columns = row.split(';');
          const entityName = columns[0] ?? '-';
          const fields = columns[1] ?? '';
          const rowNumber = columns[2] ?? '-';

          return (
            <TableRow key={index}>
              <TableCell align="center">{rowNumber}</TableCell>

              <TableCell align="center">{entityName}</TableCell>

              <TableCell align="center">
                {fields
                  .replace('[', '')
                  .replace(']', '')
                  .split(',')
                  .map((field) => field.trim())
                  .filter(Boolean)
                  .map((field) => translate(`excelImport.attributes.${field}`))
                  .join(', ')}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

const mapSymbolToKcImportType = (importType: string) => {
  if (importType === 'true') {
    return <Iconify color="green" icon="mdi:check-circle-outline" />;
  }
  return <Iconify icon="mdi:alert-circle" />;
};

const KeycloakImportNotificationContent = ({ notificationContent }: NotificationContentProp) => {
  const { translate } = useLocales();

  const parts: string[] = (notificationContent.result ?? '')
    .split('#')
    .slice(2)
    .filter((elem) => elem !== '');

  return (
    <Table size="small" stickyHeader>
      <TableHead>
        <TableRow>
          <TableCell align="left">{`${translate(
            'employeeApp.importTableHeaders.imported'
          )}`}</TableCell>
          <TableCell align="left">{`${translate(
            'employeeApp.importTableHeaders.username'
          )}`}</TableCell>
          <TableCell align="left">{`${translate(
            'employeeApp.importTableHeaders.fullName'
          )}`}</TableCell>
          <TableCell align="left">{`${translate(
            'employeeApp.importTableHeaders.errorType'
          )}`}</TableCell>
          <TableCell align="left">{`${translate(
            'employeeApp.importTableHeaders.errorHint'
          )}`}</TableCell>
        </TableRow>
      </TableHead>

      <TableBody>
        {parts.map((row, index) => {
          const columns = row.split(';');

          return (
            <TableRow key={index}>
              <TableCell align="left">{mapSymbolToKcImportType(columns[0])}</TableCell>

              <TableCell align="left">{columns[1] ?? '-'}</TableCell>

              <TableCell align="left">{columns[2] ?? '-'}</TableCell>

              <TableCell align="left">
                {`${translate(`employeeApp.importErrorType.${columns[5] ?? 'unknown'}`)}`}
              </TableCell>

              <TableCell align="left">
                {`${translate(
                  `employeeApp.importError.${mapKeycloakErrorMessage(columns[4] ?? '')}`
                )}`}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

//TODO: move to utils or dedicated helper file
const FIELD_ORDER = ['FIRSTNAME', 'LASTNAME', 'BIRTHDAY'];

//TODO: move to utils or dedicated helper file
const sortFields = (fields: string[]) =>
  [...fields].sort((a, b) => {
    const indexA = FIELD_ORDER.indexOf(a);
    const indexB = FIELD_ORDER.indexOf(b);

    if (indexA === -1 && indexB === -1) {
      return a.localeCompare(b);
    }

    if (indexA === -1) {
      return 1;
    }

    if (indexB === -1) {
      return -1;
    }

    return indexA - indexB;
  });

//TODO: move to utils or dedicated helper file
const formatValues = (
  values: Record<string, unknown> | undefined,
  translate: (key: string) => string | object
) => {
  if (!values || Object.keys(values).length === 0) {
    return '-';
  }

  return sortFields(Object.keys(values))
    .map((key) => {
      const value = values[key];
      const translatedKey = translate(`excelImport.attributes.${key}`);

      let displayValue: string;

      if (Array.isArray(value)) {
        displayValue = value.join('-');
      } else if (value === null || value === undefined || value === '') {
        displayValue = '-';
      } else {
        displayValue = String(value);
      }

      return `${translatedKey}: ${displayValue}`;
    })
    .join('\n');
};

const isConflictResolutionResolved = (notificationContent: Job) => {
  const { resultPayload } = notificationContent;

  return resultPayload?.summary?.resolved === true;
};

type ExcelImportConflictContentProps = {
  notificationContent: Job;
  setShowPopup: (active: boolean) => void;
};

const ExcelImportConflictContent = ({
  notificationContent,
  setShowPopup,
}: ExcelImportConflictContentProps) => {
  const { translate } = useLocales();

  const conflicts = React.useMemo<JobConflict[]>(() => {
    return notificationContent.resultPayload?.conflicts ?? [];
  }, [notificationContent.resultPayload]);

  const isResolved = useMemo(
    () => isConflictResolutionResolved(notificationContent),
    [notificationContent]
  );

  const [selectedActions, setSelectedActions] = React.useState<Record<number, ConflictAction>>({});
  const [overwriteValidFrom, setOverwriteValidFrom] = React.useState<Date | null>(new Date());
  const [saveExcelCustomMapping, { isLoading }] = useSaveExcelCustomMappingMutation();

  React.useEffect(() => {
    const initialActions = conflicts.reduce<Record<number, ConflictAction>>((acc, conflict) => {
      if (conflict.sourceRow !== undefined && conflict.sourceRow !== null) {
        acc[conflict.sourceRow] = 'SKIP';
      }

      return acc;
    }, {});

    setSelectedActions(initialActions);
  }, [conflicts]);

  const handleActionChange = (excelRow: number, action: ConflictAction) => {
    setSelectedActions((previousActions) => ({
      ...previousActions,
      [excelRow]: action,
    }));
  };

  const hasOverwriteSelected = useMemo(
    () =>
      conflicts.some(
        (conflict) =>
          conflict.sourceRow !== undefined &&
          conflict.sourceRow !== null &&
          selectedActions[conflict.sourceRow] === 'OVERWRITE'
      ),
    [conflicts, selectedActions]
  );

  const conflictResolution = useMemo<ConflictResolution>(() => {
    return {
      conflictJobId: notificationContent.id,
      entries: conflicts
        .filter((conflict) => conflict.sourceRow !== undefined && conflict.sourceRow !== null)
        .map((conflict) => {
          const action = selectedActions[conflict.sourceRow!] ?? 'SKIP';
          const entry = {
            excelRow: conflict.sourceRow!,
            action,
          };

          if (action === 'OVERWRITE' && overwriteValidFrom) {
            return {
              ...entry,
              overwriteValidFrom: format(overwriteValidFrom, 'yyyy-MM-dd'),
            };
          }

          return entry;
        }),
    };
  }, [conflicts, notificationContent.id, overwriteValidFrom, selectedActions]);

  const handleSubmitConflictResolution = async () => {
    try {
      const excelCustomMapping = buildExcelCustomMappingFromJobPayload(
        notificationContent.payload,
        conflictResolution
      );

      await saveExcelCustomMapping({
        importEntityType: excelCustomMapping.importEntityExcel,
        excelCustomMapping,
      }).unwrap();

      setShowPopup(false);
    } catch (error) {
      console.error(error);
    }
  };

  return (
    <>
      <Stack spacing={1}>
        <Typography variant="subtitle1">
          {`${translate('excelImport.conflictResolution.question')}`}
        </Typography>

        <Alert severity="info">
          {`${translate('excelImport.conflictResolution.description')}`}
        </Alert>

        {isResolved && (
          <Alert severity="warning">
            {`${translate('excelImport.conflictResolution.alreadyResolvedWarning')}`}
          </Alert>
        )}
      </Stack>
      <Stack spacing={2} sx={{ mt: 2, mb: 5 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell align="center">{`${translate('excelImport.row')}`}</TableCell>
              <TableCell>{`${translate('excelImport.existingEntry')}`}</TableCell>
              <TableCell>{`${translate('excelImport.importedEntry')}`}</TableCell>
              <TableCell>{`${translate('excelImport.duplicateKeyFields')}`}</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {conflicts.map((conflict, index) => {
              const excelRow = conflict.sourceRow;
              const selectedAction =
                excelRow !== undefined && excelRow !== null
                  ? (selectedActions[excelRow] ?? 'SKIP')
                  : 'SKIP';

              return (
                <TableRow
                  key={`${conflict.existingEntityId ?? 'unknown'}-${conflict.sourceRow ?? index}`}
                >
                  <TableCell align="center">{conflict.sourceRow ?? '-'}</TableCell>

                  <TableCell sx={{ whiteSpace: 'pre-line' }}>
                    {formatValues(conflict.existingValues ?? {}, translate)}
                  </TableCell>

                  <TableCell sx={{ whiteSpace: 'pre-line' }}>
                    {formatValues(conflict.incomingValues ?? {}, translate)}
                  </TableCell>

                  <TableCell sx={{ whiteSpace: 'pre-line' }}>
                    {sortFields(conflict.keyFields ?? [])
                      .map((field) => translate(`excelImport.attributes.${field}`))
                      .join('\n')}
                  </TableCell>
                  <TableCell>
                    {excelRow !== undefined && excelRow !== null ? (
                      <FormControl>
                        <RadioGroup
                          row
                          value={selectedAction}
                          onChange={(event) =>
                            handleActionChange(excelRow, event.target.value as ConflictAction)
                          }
                        >
                          <FormControlLabel
                            value="OVERWRITE"
                            control={<Radio size="small" />}
                            label={`${translate('excelImport.common.yes')}`}
                          />

                          <FormControlLabel
                            value="SKIP"
                            control={<Radio size="small" />}
                            label={`${translate('excelImport.common.no')}`}
                          />
                        </RadioGroup>
                      </FormControl>
                    ) : (
                      '-'
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        <Divider />
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="flex-end"
          alignItems={{ xs: 'stretch', sm: 'center' }}
          spacing={2}
          sx={{ mt: 5 }}
        >
          {hasOverwriteSelected && (
            <Alert severity="info" sx={{ py: 0 }}>
              {`${translate('excelImport.conflictResolution.overwriteValidFromInfo')}`}
            </Alert>
          )}

          <DatePicker
            label={`${translate('excelImport.conflictResolution.overwriteValidFromLabel')}`}
            value={overwriteValidFrom}
            onChange={(value) => setOverwriteValidFrom(value)}
            disabled={isResolved || !hasOverwriteSelected}
            format="dd.MM.yyyy"
            sx={{ width: 220 }}
          />

          <Button
            variant="contained"
            onClick={handleSubmitConflictResolution}
            disabled={
              conflicts.length === 0 ||
              isLoading ||
              isResolved ||
              (hasOverwriteSelected && !overwriteValidFrom)
            }
            sx={{ textTransform: 'none' }}
          >
            {`${translate('excelImport.conflictResolution.submit')}`}
          </Button>
        </Stack>
      </Stack>
    </>
  );
};
